import { TaskParty, TaskStatus } from '@prisma/client';
import { useContext } from 'react';

import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { cn } from '@commenda/utils';
import { ServiceRequestModel } from '@commenda/utils/models/service-request';

import { WorkflowResetToStepAction } from '../../../common/tasks/workflow/WorkflowResetToStepAction';
import {
  ServiceRequestTaskCardDisplayVariant,
  ServiceRequestTaskProps,
} from '../../../types';
import { ServiceRequestSkipAndUndoEllipsisButton } from '../../tabs/components/timeline/ServiceRequestSkipAndUndoEllipsisButton';
import { ServiceRequestStepCompletionContext } from './service-request-step-completion-context';
import { ServiceRequestTaskCompletionHelper } from './service-request-task-completion-helper';
import { ServiceRequestCardAvatar } from './ServiceRequestCardAvatar';
import { ServiceRequestTaskCompletion } from './ServiceRequestTaskCompletion';

interface ServiceRequestDefaultTaskCardProps {
  task: ServiceRequestTaskProps['task'];
  config: ServiceRequestTaskProps['config'];
  state: ServiceRequestTaskProps['state'];
  context: ServiceRequestTaskProps['context'];
}

export function ServiceRequestDefaultTaskCard({
  task,
  config,
  state,
  context,
}: ServiceRequestDefaultTaskCardProps) {
  const { enabled } = useContext(ServiceRequestStepCompletionContext);
  const stateConfig = config.getStateConfig(state);
  const statusFlags = ServiceRequestTaskCompletionHelper.getStatusFlags(
    task.status
  );
  const leftIcon = config.getLeftIcon(state.status);

  const {
    data: activityFeed,
    isLoading,
    error,
  } = useTaskActivitiesForServiceRequest(context.serviceRequest?.id);
  const taskHasOpenIssue =
    task.status !== TaskStatus.COMPLETED &&
    pairHasOpenIssue(activityFeed?.activities, task);

  const isTaskEligibleForReopenOrSkip =
    ServiceRequestModel.isTaskEligibleForReopenOrSkip(
      task,
      context.serviceRequest
    );

  const containerClasses = cn(
    'flex min-h-[56px] p-xl txt-small-medium justify-between items-center gap-md',
    context.variant === ServiceRequestTaskCardDisplayVariant.COMPLIANCE &&
      'border-none rounded-none',
    taskHasOpenIssue ? 'border-border-error' : ''
  );

  const labelClasses = cn(
    'txt-small-medium flex-1 flex items-center',
    statusFlags.isInProgress && 'text-text-warning font-medium'
  );

  return (
    <div className={containerClasses}>
      {leftIcon}
      <div className={labelClasses}>
        {enabled ? (
          <div>
            {stateConfig.label}
            <ServiceRequestTaskCompletion task={task} context={context} />
            {enabled && isLoading && (
              <span className="txt-extra-small-regular" aria-busy="true">
                Loading activity…
              </span>
            )}
            {enabled && error && (
              <span className="txt-extra-small-regular">
                Activity unavailable
              </span>
            )}
          </div>
        ) : (
          stateConfig.label
        )}
      </div>

      {state.status !== TaskStatus.BACKLOG && stateConfig.actions}

      <WorkflowResetToStepAction
        workflowExecutionId={context.workflowExecution?.id}
        targetStepDefinitionId={task.workflowDefinitionStepId}
        role={state.role}
        taskStatus={task.status}
        onResetSuccess={context.mutate}
      />

      <ServiceRequestCardAvatar state={state} task={task} context={context} />

      {state.role === TaskParty.AGENT && isTaskEligibleForReopenOrSkip && (
        <div className="flex items-center gap-md">
          <ServiceRequestSkipAndUndoEllipsisButton
            serviceRequest={context.serviceRequest}
            task={task}
            mutate={context.mutate}
          />
        </div>
      )}
    </div>
  );
}
